/** FlowSight shared front-end config (SPEC §5 theme, §4 layers). */

export const THEME = {
  bg: "#05080f",
  cyan: "#00e5ff",
  magenta: "#ff2bd6",
  warn: "#ffb020",
  overflow: "#ff3b5c",
  ok: "#22e39b",
} as const;

/** Canonical public origin — shared links always use the custom domain, even when
 *  the app is opened from the azurestaticapps.net host. */
export const CANONICAL_ORIGIN = "https://thailandfloodsight.app";

/** OpenFreeMap "dark" basemap (free, dark theme, roads + Thai/English labels). */
export const BASEMAP_STYLE = "https://tiles.openfreemap.org/styles/dark";
/** v0.9.9 B: light mode basemap — OpenFreeMap "positron" (same sprite / glyph server / sources as dark). */
export const BASEMAP_STYLE_LIGHT = "https://tiles.openfreemap.org/styles/positron";

/** Initial view — lower Chao Phraya / Gulf of Thailand (v1 focus, SPEC §1). */
export const INITIAL_VIEW = {
  center: [100.5, 14.2] as [number, number],
  zoom: 6.2,
  minZoom: 4,
  maxZoom: 13,
};

export const RIVERS = {
  pmtilesPath: "/data/rivers.pmtiles",
  sourceLayer: "rivers",
};

/** Cameras are OFF until BMA/กทม. grants written permission to show images in-app
 *  (BMA ToS forbids copying their content). Code + bma-cctv data + evidence stay in
 *  the repo behind this flag; flip to true only after permission. (v0.8.2) */
export const ENABLE_CAMERAS: boolean = false;

/** RainViewer radar switch is HIDDEN (v0.9.9a item 4): its API terms say "free for personal or
 *  educational use" (rainviewer.com/api.html) — a public website is neither clearly; and tiles stop at
 *  zoom 7 (above it the server draws "Zoom Level Not Supported"). Code stays (src/rain.ts); flip only
 *  after RainViewer confirms in writing. Report: docs/reports/FlowSight_REPORT_v0.9.9a.md */
export const ENABLE_RAINVIEWER: boolean = false;

/** Copernicus CEMS GFM (Sentinel-1 SAR) flood overlay is HIDDEN until the owner registers
 *  an EODC/GFM portal account (https://portal.gfm.eodc.eu/) and plugs the credentials into
 *  the backend (GFM_USERNAME/GFM_PASSWORD or GFM_ACCESS_TOKEN — see docs/ops/gfm-sar-setup.md).
 *  Code + proxy (/api/gfm/tile) stay in the repo behind this flag; the proxy returns nothing
 *  until configured, so the map never shows a guessed/empty tile. Flip to true only after the
 *  owner confirms the EODC WMS resolves with the live account. */
export const ENABLE_GFM_SAR: boolean = false;

/** River + canal name labels (v0.9.5c · preprocess/build_river_labels.mjs — tier minzoom baked per-feature). */
export const RIVER_LABEL_TILES = {
  pmtilesPath: "/data/river-labels.pmtiles",
  sourceLayer: "river_labels",
};

/** Credit for the OSM waterway names (ODbL). */
export const OSM_WATERWAY_ATTRIBUTION =
  'ชื่อลำน้ำ: © <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> (ODbL)';

/** River discharge → colour. Main rivers (ORD_STRA ≥5) read BRIGHT BLUE when normal
 *  so they're always legible; small tributaries stay muted grey. สูง/สูงมาก = เหลือง/แดง. */
export const DISCHARGE_COLOR: unknown[] = [
  "case", [">=", ["coalesce", ["get", "ord"], 0], 5],
  ["interpolate", ["linear"], ["coalesce", ["get", "dis_av"], 0],
    0, "#5fb8d9", 120, "#5fb8d9", 250, "#ffb020", 600, "#ff3b5c"],            // สายหลัก
  ["interpolate", ["linear"], ["coalesce", ["get", "dis_av"], 0],
    0, "#4a6b7c", 120, "#4a6b7c", 250, "#ffb020", 600, "#ff3b5c"],            // สาขาเล็ก
];
export const DISCHARGE_LEGEND = { normal: "#5fb8d9", high: "#ffb020", veryhigh: "#ff3b5c" } as const;

/** Outlet → colour (gulf highlighted, others dimmed). */
export const OUTLET_COLOR: unknown[] = [
  "match", ["get", "outlet"],
  "gulf", "#00e5ff",
  "mekong", "#5566aa",
  "andaman", "#7a5fb0",
  "salween", "#6a7a8a",
  /* other */ "#44506a",
];

export const OUTLET_LABEL_TH: Record<string, string> = {
  gulf: "อ่าวไทย",
  mekong: "แม่น้ำโขง",
  andaman: "ทะเลอันดามัน",
  salween: "สาละวิน",
  other: "อื่น ๆ / แอ่งปิด",
};

export const ATTRIBUTION =
  '<a href="https://openfreemap.org/" target="_blank" rel="noopener">OpenFreeMap</a> · ' +
  '<a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> · ' +
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> · ' +
  'Rivers: <a href="https://www.hydrosheds.org/products/hydrorivers" target="_blank" rel="noopener">HydroRIVERS</a>';
